/**
 * <quick-view-panel>: behaviour for sections/quick-view.liquid once it is inserted into the
 * shared quick view dialog (product-card.js). Handles the swipe gallery (dots, arrows, follows the
 * selected variant's image), the quantity stepper and the add to cart form.
 */
(function () {
  if (customElements.get('quick-view-panel')) return;

  const Lumi = (window.Lumi = window.Lumi || {});
  const str = (key, fallback) => (Lumi.strings && Lumi.strings[key]) || fallback;

  class QuickViewPanel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-qv-track]');
      this.slides = this.track ? [...this.track.children] : [];
      this.dots = [...this.querySelectorAll('[data-qv-dot]')];
      this.form = this.querySelector('form[data-qv-form]');
      this.error = this.querySelector('[data-qv-error]');

      this.setupGallery();
      this.setupQuantity();
      // Opened on a chosen variant (?variant=): start on its photo, as a variant change would.
      const start = this.slides.findIndex((s) => s.dataset.mediaId === this.dataset.startMedia);
      if (start > 0) this.goTo(start, true);

      // The shared quick view dialog is a bottom sheet on mobile: swipe down to dismiss.
      const dialog = this.closest('dialog');
      if (dialog && Lumi.app && Lumi.app.enableSheetSwipe) {
        Lumi.app.enableSheetSwipe(dialog, { media: '(max-width: 749px)', onClose: () => Lumi.quickView && Lumi.quickView.close() });
      }
      if (this.form) this.form.addEventListener('submit', (e) => this.submit(e));

      this.onVariant = (e) => {
        if (!e.detail || e.detail.picker == null || !this.contains(e.detail.picker)) return;
        const media = e.detail.variant && e.detail.variant.featured_media;
        if (media) this.goTo(this.slides.findIndex((s) => s.dataset.mediaId === String(media.id)));
        this.hideError();
      };
      document.addEventListener('lumi:variant:changed', this.onVariant);
    }

    disconnectedCallback() {
      document.removeEventListener('lumi:variant:changed', this.onVariant);
      if (this.io) this.io.disconnect();
    }

    /* ---------- Gallery ---------- */
    setupGallery() {
      if (this.slides.length < 2) return;
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
          });
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((s) => this.io.observe(s));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.querySelectorAll('[data-qv-step]').forEach((btn) =>
        btn.addEventListener('click', () => this.goTo((this.active || 0) + Number(btn.dataset.qvStep)))
      );
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goTo((this.active || 0) + (e.key === 'ArrowRight' ? 1 : -1));
        }
      });
    }

    setActive(index) {
      if (index < 0) return;
      this.active = index;
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
      this.querySelectorAll('[data-qv-step]').forEach((btn) => {
        const target = index + Number(btn.dataset.qvStep);
        btn.disabled = target < 0 || target >= this.slides.length;
      });
    }

    goTo(index, instant) {
      if (!this.track || index < 0 || index >= this.slides.length) return;
      const behavior = instant || (Lumi.reducedMotion && Lumi.reducedMotion()) ? 'auto' : 'smooth';
      this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior });
      this.setActive(index);
    }

    /* ---------- Quantity ---------- */
    setupQuantity() {
      const input = this.querySelector('input[name="quantity"]');
      if (!input) return;
      this.querySelectorAll('[data-qty-step]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const min = Number(input.min) || 1;
          const next = Math.max(min, (parseInt(input.value, 10) || min) + Number(btn.dataset.qtyStep));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }

    /* ---------- Add to cart ---------- */
    async submit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[data-add-button]');
      const id = Number(this.form.querySelector('input[name="id"]').value);
      const quantity = Math.max(1, parseInt((this.form.querySelector('input[name="quantity"]') || {}).value, 10) || 1);
      if (!id || (button && button.disabled)) return;

      this.hideError();
      if (button) button.setAttribute('aria-busy', 'true');
      try {
        await Lumi.cart.add([{ id, quantity }]);
        if (Lumi.quickView) await Lumi.quickView.close();
        Lumi.emit('lumi:cart:open');
        Lumi.announce(str('addedToCart', 'Added to your cart'));
      } catch (err) {
        this.showError(err && err.message);
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || str('cartError', 'That did not work. Please try again.');
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  customElements.define('quick-view-panel', QuickViewPanel);
})();
